import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/apiClient';
import type { PagedResult } from '@/api/admin';
import type { Ledger } from './journals';
import type { AccountType } from './accounting';

// ---------------------------------------------------------------------------------------------
// Types, mirroring the operations' DTOs.
// ---------------------------------------------------------------------------------------------

export interface Party {
  id: string;
  code: string;
  name: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  taxNumber: string | null;
  controlAccountId: string | null;
  controlAccountCode: string | null;
  paymentTermsDays: number;
  isActive: boolean;
  notes: string | null;
  balance: number;
  /** A supplier's withholding-tax category (AP-01); null on a customer. */
  withholdingTaxCodeId?: string | null;
  withholdingTaxCode?: string | null;
  withholdingRate?: number | null;
  /** A customer's credit terms (AR-01, AR-02). */
  creditLimit?: number;
  creditStatus?: 'Active' | 'OnHold' | 'Stopped';
  creditLimitChangedBy?: string | null;
  creditLimitChangedAt?: string | null;
  creditHoldAt?: string | null;
  creditHoldReason?: string | null;
  /** A supplier's bank details (AP-02). */
  hasVerifiedBankAccount?: boolean;
  bankChangePending?: boolean;
  /** A supplier's category, which decides whether it may bill without a purchase order (AP-10). */
  supplierCategoryId?: string | null;
  supplierCategoryCode?: string | null;
  nonPoInvoicesAllowed?: boolean;
}

export interface StatementLine {
  date: string;
  kind: string;
  number: string;
  reference: string | null;
  description: string | null;
  debit: number;
  credit: number;
  balance: number;
  documentId: string;
  status: string;
}

export interface Aging {
  current: number;
  /** Days past due: the buckets AP-16 and AR-14 name. */
  days1To30: number;
  days31To60: number;
  days61To90: number;
  days91To120: number;
  over120: number;
  total: number;
}

export interface PartyStatement { party: Party; lines: StatementLine[]; aging: Aging; asAt: string }

export interface BankAccount {
  id: string;
  code: string;
  name: string;
  bankName: string | null;
  branch: string | null;
  accountNumber: string | null;
  currency: string;
  glAccountId: string;
  glAccountCode: string;
  glAccountName: string;
  isActive: boolean;
  ledgerBalance: number;
  unreconciledLines: number;
  /** A bank account reconciled against a statement, or cash in hand that is counted. */
  kind?: BankAccountKind;
  /** Who may sign (CB-01); a float's custodian and imprest amount (CB-09). */
  signatories?: string | null;
  custodian?: string | null;
  imprestAmount?: number;
}

export type BankAccountKind = 'Bank' | 'Cash';
export const bankAccountKindLabels: Record<BankAccountKind, string> = { Bank: 'Bank account', Cash: 'Cash in hand / petty cash' };

/** The cash book of one account: its ledger over a range with the balances either side. */
export interface CashBookStatement {
  bankAccountId: string;
  code: string;
  name: string;
  kind: BankAccountKind;
  currency: string;
  ledger: Ledger;
  unreconciledLines: number;
}

export type InvoiceKind = 'Sales' | 'Purchase';
export type InvoiceStatus = 'Draft' | 'Issued' | 'PartiallyPaid' | 'Paid' | 'Cancelled' | 'WrittenOff';

export interface InvoiceLine {
  id: string;
  lineNo: number;
  description: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  costCentreId: string | null;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  lineTotal: number;
  taxAmount: number;
  /** The purchase-order line it bills (AP-09). */
  purchaseOrderLineId?: string | null;
  /** The project (initiative) the line belongs to, and what the delivery notes have taken out. */
  initiativeId?: string | null; capitalProjectId?: string | null; wbsElementId?: string | null;
  quantityDelivered?: number;
  /** The tax code the line was priced under and its kind (TX-01). */
  taxCodeId?: string | null;
  taxCode?: string | null;
  taxKind?: 'Standard' | 'ZeroRated' | 'Exempt' | 'ReverseCharge' | 'Withholding' | 'Excise';
}

export interface Invoice {
  id: string;
  kind: InvoiceKind;
  isCreditNote: boolean;
  number: string | null;
  date: string;
  dueDate: string;
  partyId: string;
  partyCode: string;
  partyName: string;
  reference: string | null;
  description: string | null;
  status: InvoiceStatus;
  subtotal: number;
  taxTotal: number;
  total: number;
  amountPaid: number;
  balance: number;
  journalEntryId: string | null;
  journalNumber: string | null;
  issuedBy: string | null;
  issuedAt: string | null;
  cancelledBy: string | null;
  cancelledAt: string | null;
  cancelledReason: string | null;
  creditedInvoiceId: string | null;
  creditedInvoiceNumber: string | null;
  /** The order a purchase invoice bills and where it stands against it and the receipts (AP-09). */
  purchaseOrderId?: string | null;
  purchaseOrderNumber?: string | null;
  matchStatus?: 'None' | 'Matched' | 'Exception' | 'Accepted';
  matchNote?: string | null;
  matchedAt?: string | null;
  /** The quotation a sales invoice came from, where its goods stand, when it was emailed, its attachments, and a bill's budget override (§136). */
  quotationId?: string | null;
  quotationNumber?: string | null;
  deliveryStatus?: 'NotApplicable' | 'Pending' | 'Dispatched' | 'Delivered';
  emailedTo?: string | null;
  emailedAt?: string | null;
  attachments?: number;
  budgetOverrideReason?: string | null;
  budgetOverriddenBy?: string | null;
  /** A bad-debt write-off (AR-15). */
  writeOffJournalId?: string | null;
  writeOffJournalNumber?: string | null;
  writtenOffAmount?: number;
  writtenOffBy?: string | null;
  writtenOffAt?: string | null;
  writeOffReason?: string | null;
  createdBy: string | null;
  createdDate: string;
  lines: InvoiceLine[];
}

export interface InvoiceListItem {
  id: string;
  kind: InvoiceKind;
  isCreditNote: boolean;
  number: string | null;
  date: string;
  dueDate: string;
  partyId: string;
  partyCode: string;
  partyName: string;
  reference: string | null;
  description: string | null;
  status: InvoiceStatus;
  total: number;
  amountPaid: number;
  balance: number;
  overdue: boolean;
  deliveryStatus?: 'NotApplicable' | 'Pending' | 'Dispatched' | 'Delivered';
  emailedAt?: string | null;
  matchStatus?: 'None' | 'Matched' | 'Exception' | 'Accepted';
}

/** With a tax code the rate is the code's on the document's date and taxRate is ignored by the server. */
export interface InvoiceLineInput { description: string; accountId: string; quantity: number; unitPrice: number; taxRate: number; costCentreId: string | null; taxCodeId?: string | null; purchaseOrderLineId?: string | null; initiativeId?: string | null; capitalProjectId?: string | null; wbsElementId?: string | null;
  /** The stock item the line sells or buys, where the organisation keeps stock (stock feedback, 7 Oct 2026). */
  stockItemId?: string | null }

export interface InvoiceInput {
  kind: InvoiceKind;
  isCreditNote: boolean;
  date: string;
  dueDate: string | null;
  partyId: string;
  reference: string | null;
  description: string | null;
  lines: InvoiceLineInput[];
  creditedInvoiceId: string | null;
  /** A purchase invoice that looks like one already captured is refused unless a reason is given (AP-11). */
  duplicateOverrideReason?: string | null;
  /** On a purchase invoice: the issued order it bills (AP-09); null is a non-PO invoice (AP-10). */
  purchaseOrderId?: string | null;
}

export type PaymentKind = 'Receipt' | 'Payment';
export type PaymentMethod = 'Cash' | 'Cheque' | 'Eft' | 'MobileMoney' | 'Card';
export type PaymentStatus = 'Draft' | 'Posted' | 'Cancelled';
export const paymentMethods: PaymentMethod[] = ['Eft', 'Cash', 'Cheque', 'MobileMoney', 'Card'];

export interface Allocation { id: string; invoiceId: string; invoiceNumber: string | null; invoiceTotal: number; invoiceBalance: number; amount: number }

export interface Payment {
  id: string;
  kind: PaymentKind;
  number: string | null;
  date: string;
  /** Null for a cash-book entry, which names an account instead. */
  partyId: string | null;
  partyCode: string | null;
  partyName: string | null;
  contraAccountId?: string | null;
  contraAccountCode?: string | null;
  contraAccountName?: string | null;
  costCentreId?: string | null;
  costCentreCode?: string | null;
  bankAccountId: string;
  bankAccountCode: string;
  bankAccountName: string;
  amount: number;
  allocated: number;
  unallocated: number;
  method: PaymentMethod;
  reference: string | null;
  description: string | null;
  status: PaymentStatus;
  journalEntryId: string | null;
  journalNumber: string | null;
  postedBy: string | null;
  postedAt: string | null;
  cancelledBy: string | null;
  cancelledAt: string | null;
  cancelledReason: string | null;
  reconciled: boolean;
  createdBy: string | null;
  createdDate: string;
  allocations: Allocation[];
  /** Withholding tax on a supplier payment (AP-12): the amount is the gross settled, the bank pays the net. */
  withholdingTaxCodeId?: string | null;
  withholdingTaxCode?: string | null;
  withholdingRate?: number;
  withholdingAmount?: number;
  netPaid?: number;
  certificateNumber?: string | null;
  initiativeId?: string | null; capitalProjectId?: string | null; wbsElementId?: string | null;
}

export interface PaymentListItem {
  id: string;
  kind: PaymentKind;
  number: string | null;
  date: string;
  partyId: string | null;
  partyCode: string | null;
  partyName: string | null;
  contraAccountId?: string | null;
  contraAccountCode?: string | null;
  contraAccountName?: string | null;
  bankAccountCode: string;
  amount: number;
  allocated: number;
  method: PaymentMethod;
  reference: string | null;
  status: PaymentStatus;
  withholdingAmount?: number;
  certificateNumber?: string | null;
}

export interface PaymentInput {
  kind: PaymentKind;
  date: string;
  /** Either the customer or supplier settled, or (null) the account a cash-book entry is coded to. */
  partyId: string | null;
  contraAccountId?: string | null;
  bankAccountId: string;
  amount: number;
  method: PaymentMethod;
  reference: string | null;
  description: string | null;
  allocations: { invoiceId: string; amount: number }[];
  /** For a cash-book entry: the project (initiative) it belongs to. */
  initiativeId?: string | null; capitalProjectId?: string | null; wbsElementId?: string | null;
  /** For a cash-book entry: where the income or expenditure is attributed; a payment settling documents carries none. */
  costCentreId?: string | null;
  /** Deduct the supplier's withholding category (the default when they have one); false pays the gross. */
  withholdTax?: boolean;
}

export interface BankTransaction {
  id: string;
  bankAccountId: string;
  date: string;
  description: string;
  reference: string | null;
  amount: number;
  source: 'Manual' | 'Import';
  isReconciled: boolean;
  matchedLedgerEntryId: string | null;
  matchedJournalNumber: string | null;
  reconciledAt: string | null;
  reconciledBy: string | null;
  /** The statement file the line came from, when it came from one; such a line is voided with its import, not edited alone. */
  importId: string | null;
  /** The running balance the statement showed after this line, where the file carried one. */
  statementBalance: number | null;
}

export interface BankTransactionInput { date: string; description: string; amount: number; reference: string | null; importKey: string | null }

// ---------------------------------------------------------------------------------------------
// Statement files (CB-02, CB-03) and the reconciliation parameters (CB-04)
// ---------------------------------------------------------------------------------------------

/** How a bank's statement export is read. A column is named by its header, or "#n" when the file has none. */
export interface StatementLayout {
  dateColumn: string;
  descriptionColumn: string;
  referenceColumn: string | null;
  amountColumn: string | null;
  debitColumn: string | null;
  creditColumn: string | null;
  balanceColumn: string | null;
  dateFormat: string | null;
  negativeIsMoneyOut: boolean;
  debitIsMoneyOut: boolean;
  headerRows: number;
  sheetName: string | null;
  delimiter: string | null;
  skipPatterns: string | null;
  name: string;
}

export interface ParsedStatementLine {
  rowNumber: number;
  date: string | null;
  description: string;
  reference: string | null;
  amount: number | null;
  balance: number | null;
  problems: string[];
  skipped: boolean;
  skipReason: string | null;
  alreadyOnAccount: boolean;
}

export interface StatementPreview {
  fileName: string;
  sizeBytes: number;
  sha256: string;
  fileAlreadyImported: boolean;
  sheets: string[];
  sheet: string;
  columns: string[];
  sampleRows: string[][];
  layout: StatementLayout;
  layoutIsSaved: boolean;
  lines: ParsedStatementLine[];
  totalLines: number;
  readableLines: number;
  problemLines: number;
  skippedLines: number;
  alreadyOnAccount: number;
  moneyIn: number;
  moneyOut: number;
  from: string | null;
  to: string | null;
  openingBalance: number | null;
  closingBalance: number | null;
  balanceAgrees: boolean | null;
  newestFirst: boolean;
  warnings: string[];
}

export interface StatementImport {
  id: string;
  bankAccountId: string;
  fileName: string;
  sizeBytes: number;
  sha256: string;
  evidenceDocumentId: string | null;
  fiscalPeriodId: string | null;
  periodName: string | null;
  statementFrom: string | null;
  statementTo: string | null;
  openingBalance: number | null;
  closingBalance: number | null;
  lineCount: number;
  skippedCount: number;
  moneyIn: number;
  moneyOut: number;
  status: 'Imported' | 'Voided';
  importedBy: string | null;
  importedAt: string;
  voidedBy: string | null;
  voidedAt: string | null;
  voidedReason: string | null;
  reconciledLines: number;
  unreconciledLines: number;
}

export type ReferenceMatchMode = 'Ignore' | 'Prefer' | 'Require';
export type RuleMatchField = 'Either' | 'Description' | 'Reference';

export interface ReconciliationRule {
  id: string | null;
  sequence: number;
  name: string;
  pattern: string;
  matchOn: RuleMatchField;
  action: 'PostToAccount';
  contraAccountId: string | null;
  contraAccountCode: string | null;
  contraAccountName: string | null;
  memo: string | null;
  isActive: boolean;
  costCentreId?: string | null;
  costCentreCode?: string | null;
}

export interface ReconciliationSettings {
  bankAccountId: string;
  matchDayTolerance: number;
  matchAmountTolerance: number;
  matchReferenceMode: ReferenceMatchMode;
  rules: ReconciliationRule[];
  autoReceiptFromStatement?: boolean;
}

export interface ReconciliationSettingsInput {
  matchDayTolerance: number;
  matchAmountTolerance: number;
  matchReferenceMode: ReferenceMatchMode;
  rules: ReconciliationRule[];
  autoReceiptFromStatement?: boolean;
}

export interface AutoMatchResult { matched: number; remaining: number; created: number; notes: string[] | null }

export interface UnmatchedLedgerEntry { id: string; date: string; journalNumber: string; description: string | null; amount: number }

export interface Reconciliation {
  bankAccount: BankAccount;
  from: string | null;
  to: string | null;
  ledgerBalance: number;
  statementBalance: number;
  reconciledStatementBalance: number;
  unreconciledStatementTotal: number;
  unmatchedLedgerTotal: number;
  unreconciledTransactions: BankTransaction[];
  unmatchedLedgerEntries: UnmatchedLedgerEntry[];
}

export type { AccountType };

// ---------------------------------------------------------------------------------------------
// Hooks. Everything under ['accounting'] is invalidated by any accounting mutation.
// ---------------------------------------------------------------------------------------------

const base = '/api/v1/accounting';

function useOpsMutation<TInput, TOut>(fn: (input: TInput) => Promise<TOut>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting'] }) });
}

function params(obj: Record<string, string | number | boolean | undefined | null>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) if (v !== undefined && v !== null && v !== '') out[k] = String(v);
  return out;
}

export function useParties(kind: 'customers' | 'suppliers', q: { page: number; pageSize: number; search?: string; isActive?: boolean }) {
  return useQuery({
    queryKey: ['accounting', kind, q],
    queryFn: async () => (await apiClient.get<PagedResult<Party>>(`${base}/${kind}`, { params: params(q) })).data,
    placeholderData: (prev) => prev,
  });
}

export function usePartyStatement(kind: 'customers' | 'suppliers', id: string | null, asAt?: string) {
  return useQuery({
    queryKey: ['accounting', kind, 'statement', id, asAt ?? null],
    queryFn: async () => (await apiClient.get<PartyStatement>(`${base}/${kind}/${id}/statement`, { params: params({ asAt }) })).data,
    enabled: id !== null,
  });
}

/** The cash book of one account over a range: the balance brought forward, every entry with its running balance, the balance carried down. */
export function useCashBookStatement(bankAccountId: string | null, range: { from?: string; to?: string } = {}) {
  return useQuery({
    queryKey: ['accounting', 'cash-book', bankAccountId, range],
    queryFn: async () => (await apiClient.get<CashBookStatement>(`${base}/bank-accounts/${bankAccountId}/statement`, { params: params(range) })).data,
    enabled: bankAccountId !== null,
  });
}

/** The cash-book record's own details; the account's name, class and category are the chart's. */
export interface BankAccountInput {
  code: string;
  name: string;
  glAccountId: string;
  bankName?: string | null;
  branch?: string | null;
  accountNumber?: string | null;
  currency?: string;
  isActive?: boolean;
  kind?: BankAccountKind;
  signatories?: string | null;
  custodian?: string | null;
  imprestAmount?: number;
}

export const useUpdateBankAccount = () =>
  useOpsMutation(async ({ id, input }: { id: string; input: BankAccountInput }) => (await apiClient.put<BankAccount>(`${base}/bank-accounts/${id}`, input)).data);

export function useBankAccounts(q: { page: number; pageSize: number; search?: string; isActive?: boolean } = { page: 1, pageSize: 100 }) {
  return useQuery({
    queryKey: ['accounting', 'bank-accounts', q],
    queryFn: async () => (await apiClient.get<PagedResult<BankAccount>>(`${base}/bank-accounts`, { params: params(q) })).data,
    placeholderData: (prev) => prev,
  });
}

export function useInvoices(q: { page: number; pageSize: number; search?: string; kind?: InvoiceKind; status?: InvoiceStatus | ''; partyId?: string; open?: boolean; isCreditNote?: boolean }) {
  return useQuery({
    queryKey: ['accounting', 'invoices', q],
    queryFn: async () => (await apiClient.get<PagedResult<InvoiceListItem>>(`${base}/invoices`, { params: params(q) })).data,
    placeholderData: (prev) => prev,
  });
}

export function useInvoice(id: string | null) {
  return useQuery({
    queryKey: ['accounting', 'invoices', 'one', id],
    queryFn: async () => (await apiClient.get<Invoice>(`${base}/invoices/${id}`)).data,
    enabled: id !== null,
  });
}

export const useCreateInvoice = () => useOpsMutation(async (input: InvoiceInput) => (await apiClient.post<Invoice>(`${base}/invoices`, input)).data);
export const useUpdateInvoice = () => useOpsMutation(async ({ id, input }: { id: string; input: InvoiceInput }) => (await apiClient.put<Invoice>(`${base}/invoices/${id}`, input)).data);
export const useDeleteInvoice = () => useOpsMutation(async (id: string) => { await apiClient.delete(`${base}/invoices/${id}`); });
export const useIssueInvoice = () => useOpsMutation(async ({ id, creditOverrideReason, budgetOverrideReason }: { id: string; creditOverrideReason?: string | null; budgetOverrideReason?: string | null }) => (await apiClient.post<Invoice>(`${base}/invoices/${id}/issue`, { creditOverrideReason: creditOverrideReason ?? null, budgetOverrideReason: budgetOverrideReason ?? null })).data);
export const useVoidInvoice = () => useOpsMutation(async ({ id, reason, reasonCodeId }: { id: string; reason: string; reasonCodeId: string }) => (await apiClient.post<Invoice>(`${base}/invoices/${id}/void`, { reason, reasonCodeId })).data);

export function usePayments(q: { page: number; pageSize: number; search?: string; kind?: PaymentKind; status?: PaymentStatus | ''; partyId?: string; bankAccountId?: string; hasParty?: boolean }) {
  return useQuery({
    queryKey: ['accounting', 'payments', q],
    queryFn: async () => (await apiClient.get<PagedResult<PaymentListItem>>(`${base}/payments`, { params: params(q) })).data,
    placeholderData: (prev) => prev,
  });
}

export function usePayment(id: string | null) {
  return useQuery({
    queryKey: ['accounting', 'payments', 'one', id],
    queryFn: async () => (await apiClient.get<Payment>(`${base}/payments/${id}`)).data,
    enabled: id !== null,
  });
}

export const useCreatePayment = () => useOpsMutation(async (input: PaymentInput) => (await apiClient.post<Payment>(`${base}/payments`, input)).data);
export const useUpdatePayment = () => useOpsMutation(async ({ id, input }: { id: string; input: PaymentInput }) => (await apiClient.put<Payment>(`${base}/payments/${id}`, input)).data);
export const useDeletePayment = () => useOpsMutation(async (id: string) => { await apiClient.delete(`${base}/payments/${id}`); });
export const usePostPayment = () => useOpsMutation(async (id: string) => (await apiClient.post<Payment>(`${base}/payments/${id}/post`)).data);
export const useVoidPayment = () => useOpsMutation(async ({ id, reason, reasonCodeId }: { id: string; reason: string; reasonCodeId: string }) => (await apiClient.post<Payment>(`${base}/payments/${id}/void`, { reason, reasonCodeId })).data);

export function useBankTransactions(bankAccountId: string | null, q: { page: number; pageSize: number; search?: string; reconciled?: boolean }) {
  return useQuery({
    queryKey: ['accounting', 'bank-transactions', bankAccountId, q],
    queryFn: async () => (await apiClient.get<PagedResult<BankTransaction>>(`${base}/bank-accounts/${bankAccountId}/transactions`, { params: params(q) })).data,
    enabled: bankAccountId !== null,
    placeholderData: (prev) => prev,
  });
}

export const useAddBankTransactions = () =>
  useOpsMutation(async ({ bankAccountId, lines, source }: { bankAccountId: string; lines: BankTransactionInput[]; source: 'Manual' | 'Import' }) =>
    (await apiClient.post<{ added: number; skipped: number }>(`${base}/bank-accounts/${bankAccountId}/transactions`, lines, { params: { source } })).data);
export const useDeleteBankTransaction = () =>
  useOpsMutation(async ({ bankAccountId, id }: { bankAccountId: string; id: string }) => { await apiClient.delete(`${base}/bank-accounts/${bankAccountId}/transactions/${id}`); });

export function useReconciliation(bankAccountId: string | null, from?: string, to?: string) {
  return useQuery({
    queryKey: ['accounting', 'reconciliation', bankAccountId, from ?? null, to ?? null],
    queryFn: async () => (await apiClient.get<Reconciliation>(`${base}/bank-accounts/${bankAccountId}/reconciliation`, { params: params({ from, to }) })).data,
    enabled: bankAccountId !== null,
  });
}

export const useMatchTransaction = () =>
  useOpsMutation(async ({ bankAccountId, id, ledgerEntryId }: { bankAccountId: string; id: string; ledgerEntryId: string }) =>
    (await apiClient.post<BankTransaction>(`${base}/bank-accounts/${bankAccountId}/transactions/${id}/match`, { ledgerEntryId })).data);
export const useUnmatchTransaction = () =>
  useOpsMutation(async ({ bankAccountId, id }: { bankAccountId: string; id: string }) =>
    (await apiClient.post<BankTransaction>(`${base}/bank-accounts/${bankAccountId}/transactions/${id}/unmatch`)).data);
export const useAutoMatch = () =>
  useOpsMutation(async ({ bankAccountId, applyRules = true }: { bankAccountId: string; applyRules?: boolean }) =>
    (await apiClient.post<AutoMatchResult>(`${base}/bank-accounts/${bankAccountId}/reconciliation/auto-match`, null, { params: { applyRules } })).data);

export function useReconciliationSettings(bankAccountId: string | null) {
  return useQuery({
    queryKey: ['accounting', 'reconciliation-settings', bankAccountId],
    queryFn: async () => (await apiClient.get<ReconciliationSettings>(`${base}/bank-accounts/${bankAccountId}/reconciliation/settings`)).data,
    enabled: bankAccountId !== null,
  });
}
export const useSaveReconciliationSettings = () =>
  useOpsMutation(async ({ bankAccountId, input }: { bankAccountId: string; input: ReconciliationSettingsInput }) =>
    (await apiClient.put<ReconciliationSettings>(`${base}/bank-accounts/${bankAccountId}/reconciliation/settings`, input)).data);

export function useStatementLayout(bankAccountId: string | null) {
  return useQuery({
    queryKey: ['accounting', 'statement-layout', bankAccountId],
    queryFn: async () => {
      const r = await apiClient.get<StatementLayout | ''>(`${base}/bank-accounts/${bankAccountId}/statement-layout`);
      return r.status === 204 || !r.data ? null : r.data;
    },
    enabled: bankAccountId !== null,
  });
}
export const useSaveStatementLayout = () =>
  useOpsMutation(async ({ bankAccountId, layout }: { bankAccountId: string; layout: StatementLayout }) =>
    (await apiClient.put<StatementLayout>(`${base}/bank-accounts/${bankAccountId}/statement-layout`, layout)).data);

/** Reads the file under the layout given (or the saved / guessed one) and says what would be imported. Nothing is written, so no cache is touched. */
export const usePreviewStatement = () =>
  useMutation({
    mutationFn: async ({ bankAccountId, file, layout }: { bankAccountId: string; file: File; layout?: StatementLayout | null }) => {
      const form = new FormData();
      form.append('file', file);
      if (layout) form.append('layout', JSON.stringify(layout));
      return (await apiClient.post<StatementPreview>(`${base}/bank-accounts/${bankAccountId}/statement-imports/preview`, form)).data;
    },
  });
export const useImportStatement = () =>
  useOpsMutation(async ({ bankAccountId, file, layout, fiscalPeriodId, saveLayout }: { bankAccountId: string; file: File; layout: StatementLayout; fiscalPeriodId?: string | null; saveLayout: boolean }) => {
    const form = new FormData();
    form.append('file', file);
    form.append('layout', JSON.stringify(layout));
    if (fiscalPeriodId) form.append('fiscalPeriodId', fiscalPeriodId);
    form.append('saveLayout', String(saveLayout));
    return (await apiClient.post<StatementImport>(`${base}/bank-accounts/${bankAccountId}/statement-imports`, form)).data;
  });
export function useStatementImports(bankAccountId: string | null) {
  return useQuery({
    queryKey: ['accounting', 'statement-imports', bankAccountId],
    queryFn: async () => (await apiClient.get<StatementImport[]>(`${base}/bank-accounts/${bankAccountId}/statement-imports`)).data,
    enabled: bankAccountId !== null,
  });
}
export const useVoidStatementImport = () =>
  useOpsMutation(async ({ bankAccountId, importId, reason, reasonCodeId }: { bankAccountId: string; importId: string; reason: string; reasonCodeId: string }) =>
    (await apiClient.post<StatementImport>(`${base}/bank-accounts/${bankAccountId}/statement-imports/${importId}/void`, { reason, reasonCodeId })).data);
